import React, { useState, useEffect, useCallback, useRef } from "react";

interface TypewriterTextProps {
  messages: string[];
  typingSpeed?: number;
  delayBetweenMessages?: number;
}

const TypewriterText: React.FC<TypewriterTextProps> = ({
  messages,
  typingSpeed = 50,
  delayBetweenMessages = 2000,
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const [showCursor, setShowCursor] = useState(true);

  // Store the current message for debugging
  const currentMessage = messages[currentMessageIndex];

  // Use refs to ensure we're using the latest props in our timeouts
  const delayRef = useRef(delayBetweenMessages);
  delayRef.current = delayBetweenMessages;

  // Add randomized typing delay for realism
  const getRandomTypingDelay = useCallback(() => {
    // Vary typing speed by ±30% for more realistic typing
    const variance = typingSpeed * 0.3;
    return Math.max(10, typingSpeed - variance + Math.random() * variance * 2);
  }, [typingSpeed]);

  // Separate effect for handling the typing phase
  useEffect(() => {
    if (!isTyping || !currentMessage) return;

    if (displayedText.length < currentMessage.length) {
      const timeoutId = setTimeout(() => {
        setDisplayedText(currentMessage.slice(0, displayedText.length + 1));
      }, getRandomTypingDelay());
      return () => clearTimeout(timeoutId);
    } else {
      // Complete message has been typed
      const timeoutId = setTimeout(() => {
        setIsTyping(false);
      }, 500); // Short pause at the end of typing
      return () => clearTimeout(timeoutId);
    }
  }, [displayedText, isTyping, currentMessage, getRandomTypingDelay]);

  // Separate effect for handling the pause between messages
  useEffect(() => {
    if (isTyping || !currentMessage) return;

    const timeoutId = setTimeout(() => {
      // Move to the next message
      setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % messages.length);
      setDisplayedText("");
      setIsTyping(true);
    }, delayRef.current);

    return () => clearTimeout(timeoutId);
  }, [isTyping, messages.length, currentMessage]);

  // Blinking cursor effect
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 500);
    return () => clearInterval(cursorInterval);
  }, []);

  // Log current state for debugging
  console.log(
    `Current message: "${currentMessage}", Index: ${currentMessageIndex}, Total: ${messages.length}`
  );

  return (
    <div className="h-12 flex items-center justify-center bg-slate-800/40 rounded-md px-4 backdrop-blur-sm">
      <div className="text-lg font-mono text-indigo-400 flex items-center">
        <span className="min-h-[1.5rem] inline-block">{displayedText}</span>
        <span
          className={`ml-0.5 h-5 w-2.5 bg-indigo-400 ${
            showCursor ? "opacity-100" : "opacity-0"
          } transition-opacity`}
        />
      </div>
    </div>
  );
};

export default TypewriterText;
